// Calls to the account service's billing routes (api/src/routes/billing.ts).
//
// The browser never sees a Stripe URL it could be tricked into, a price id, or
// an API key: it asks for a checkout or a portal for the signed-in account, and
// is told where to go. Which Stripe account, which price, and whether this
// account may buy at all are all the server's answer.

import type { BillingInterval } from "./config/plans";
import { clearHostSession, getStoredHostSession } from "./webAuth";

const ACCOUNT_URL = process.env.REACT_APP_QA_API_URL ?? "";

export type BillingError =
  | "sign_in_required"
  | "already_pro"
  | "no_billing_account"
  | "billing_unavailable"
  | "no_subscription"
  | "failed";

export type BillingResult = { ok: true; url: string } | { ok: false; error: BillingError };

async function post(path: string, body?: unknown): Promise<BillingResult> {
  const session = getStoredHostSession();
  if (!session) return { ok: false, error: "sign_in_required" };
  if (!ACCOUNT_URL) return { ok: false, error: "billing_unavailable" };
  try {
    const response = await fetch(`${ACCOUNT_URL}${path}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${session.sessionToken}`,
        ...(body ? { "Content-Type": "application/json" } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const json = await response.json().catch(() => ({}));
    if (response.ok && typeof json.url === "string") return { ok: true, url: json.url };
    const known: BillingError[] = [
      "sign_in_required",
      "already_pro",
      "no_billing_account",
      "billing_unavailable",
      "no_subscription",
    ];
    return { ok: false, error: known.includes(json.error) ? json.error : "failed" };
  } catch {
    return { ok: false, error: "failed" };
  }
}

/** A Stripe Checkout URL for the signed-in account and the chosen interval. */
export const startCheckout = (interval: BillingInterval, source: string) =>
  post("/billing/checkout", { interval, source });

/**
 * A Stripe customer-portal URL: change plan, update card, cancel, invoices.
 * `flow` opens it straight on one task; `returnTo: "billing"` comes back to the
 * Billing page instead of /upgrade.
 */
export const openBillingPortal = (options?: { flow?: "cancel" | "card"; returnTo?: "billing" }) =>
  post("/billing/portal", options);

export type Account = {
  sub: string;
  email: string;
  planSource: "none" | "paid";
  // Additive fields from a newer account service; absent on an older one.
  billing?: "lifetime" | "subscription" | null;
  subscription?: {
    interval: BillingInterval | null;
    status: string;
    cancelAtPeriodEnd: boolean;
    currentPeriodEnd: string | null;
    paidThrough: string | null;
  } | null;
  canManageBilling?: boolean;
};

/**
 * The signed-in account as the SERVER sees it right now — never an email URL
 * parameter or a cached plan, so a forwarded email can't upgrade its original
 * recipient by accident. Null when nobody is signed in (a rejected token is
 * cleared on the way). Throws when the account service can't answer.
 */
export async function fetchAccount(signal?: AbortSignal): Promise<Account | null> {
  const session = getStoredHostSession();
  if (!session) return null;
  if (!ACCOUNT_URL) throw new Error("Account service unavailable");
  const response = await fetch(`${ACCOUNT_URL}/account/me`, {
    headers: { Authorization: `Bearer ${session.sessionToken}` },
    signal,
  });
  if (response.status === 401) {
    clearHostSession();
    return null;
  }
  if (!response.ok) throw new Error("Account check failed");
  const result = await response.json();
  if (!result.sub || !["none", "paid"].includes(result.planSource)) {
    throw new Error("Invalid account response");
  }
  return result;
}
